import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
    <span className="-mb-1 block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yBg = useTransform(scrollYProgress, [0, 1], [0, 160]);
    const yCard = useTransform(scrollYProgress, [0, 1], [0, -70]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="grain relative flex min-h-screen items-center overflow-hidden bg-[#0A1224]"
        >
            <motion.div style={{ y: yBg }} className="absolute inset-0 scale-110">
                <img
                    src="/images/hero.jpg"
                    alt="Class B garden-style multifamily community in a Sunbelt market at golden hour"
                    className="h-full w-full object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A1224] via-[#0A1224]/85 to-[#0A1224]/35" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1224] via-transparent to-[#0A1224]/70" />
            </motion.div>

            <svg
                className="pointer-events-none absolute -top-24 right-0 hidden h-[420px] w-[720px] opacity-50 sm:block"
                viewBox="0 0 720 420"
                fill="none"
            >
                <motion.path
                    d="M40 400 C 220 60, 520 -40, 760 60"
                    stroke="#1BA8E0"
                    strokeWidth="2"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 2.2, delay: 0.4, ease: "easeInOut" }}
                />
            </svg>

            <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-5 pb-28 pt-40 sm:px-8 lg:grid-cols-12 lg:px-12">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
                        className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-[#1BA8E0]"
                    >
                        <span className="h-px w-10 bg-[#1BA8E0]" />
                        Sunbelt Multifamily Operations
                    </motion.p>
                    <h1
                        data-testid="hero-headline"
                        className="mt-8 font-display text-[2.9rem] font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl"
                    >
                        <Line delay={0.35}>Better Management.</Line>
                        <Line delay={0.5}>
                            <span className="text-[#1BA8E0]">Stronger NOI.</span>
                        </Line>
                        <Line delay={0.65}>Greater Asset Value.</Line>
                    </h1>
                    <motion.p
                        data-testid="hero-subtext"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
                        className="mt-8 max-w-md text-base leading-relaxed text-slate-300 sm:text-lg"
                    >
                        Specialized in Class B &amp; C multifamily communities across Sunbelt markets.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-contact-cta"
                            onClick={() => scrollToId("contact")}
                            className="group inline-flex items-center gap-2 rounded-full bg-[#1BA8E0] px-7 py-3.5 text-sm font-semibold text-[#06121F] transition-colors duration-300 hover:bg-white"
                        >
                            Let&rsquo;s Improve Your Property Performance
                            <ArrowUpRight
                                size={17}
                                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                            />
                        </button>
                        <button
                            data-testid="hero-explore-cta"
                            onClick={() => scrollToId("focus")}
                            className="rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:border-[#1BA8E0] hover:text-[#1BA8E0]"
                        >
                            Explore Our Focus
                        </button>
                    </motion.div>
                </div>

                <div className="hidden lg:col-span-5 lg:block">
                    <motion.div
                        style={{ y: yCard }}
                        initial={{ opacity: 0, x: 70, rotate: 4 }}
                        animate={{ opacity: 1, x: 0, rotate: 2 }}
                        transition={{ duration: 1.2, delay: 0.85, ease: EASE }}
                        className="relative"
                    >
                        <div className="absolute -inset-8 rounded-[2.5rem] bg-[#1BA8E0]/15 blur-3xl" />
                        <div className="relative overflow-hidden rounded-[1.75rem] border border-white/15 shadow-2xl shadow-black/50">
                            <img
                                src="/images/courtyard.jpg"
                                alt="Pool courtyard of a Class B garden-style apartment community"
                                className="aspect-[4/5] w-full object-cover"
                            />
                            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-[#060C1B]/90 to-transparent px-6 pb-5 pt-16">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-300">
                                    Class B &amp; C Communities
                                </p>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C5A059]">
                                    Sunbelt
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
            >
                <div className="flex flex-col items-center gap-2 text-slate-400">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.3em]">Scroll</span>
                    <ArrowDown size={16} className="animate-bounce" />
                </div>
            </motion.div>
        </section>
    );
}
